import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { buildBrandedCloudinaryImageTransforms } from '@/layout/Seo/SeoHelper'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getOffersForProject, Offer } from '@/services/PifService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PayItForwardExpressCheckoutView } from '@/views/PayItForward/PayItForwardExpressCheckoutView'

interface PayItForwardProjectCheckoutPageProps {
  cloudinaryImageTransformation: string
  locale: string
  offers: Offer[]
  projectMeta: ProjectMeta
}

export const PayItForwardProjectCheckoutPage: NextPageWithLayout<PayItForwardProjectCheckoutPageProps> = ({
  cloudinaryImageTransformation,
  locale,
  offers,
  projectMeta,
}) => {
  const { t } = useTranslate('pif')

  const projectName = projectMeta.name
  const optionalDescription = t(
    'keepProjectFreeAndHelpCreate',
    'Keep {{projectName}} free and help create future seasons.',
    {
      projectName,
    },
  )

  return (
    <div className="bg-gray-100">
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: projectMeta.discoveryPosterLandscapeCloudinaryPath,
          cloudinaryTransformation: cloudinaryImageTransformation,
          description: projectMeta.pifExplainerText || optionalDescription,
          locale,
          path: `${paths.payItForward.index}/${projectMeta.slug}`,
          title: t('payItForwardForProject', 'Pay it Forward for {{projectName}} on Angel Studios', { projectName }),
        })}
      />
      <ProjectMetaProvider project={projectMeta}>
        <MarketingVideoPopup />
        <PayItForwardExpressCheckoutView project={projectMeta} offers={offers} />
      </ProjectMetaProvider>
    </div>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, params, preview }) => {
    const slug = params?.projectSlug as string

    const projectMeta = await getProjectMeta({ slug }, federationClient, { locale, preview })
    const offers = await getOffersForProject({ projectSlug: slug }, federationClient)

    if (!offers?.length || !projectMeta || !projectMeta.pifEnabled) {
      return {
        redirect: {
          destination: paths.payItForward.forYourConsideration,
          permanent: false,
        },
      }
    }

    // TODO Remove once SOF is out of theaters
    const cloudinaryImageTransformation = buildBrandedCloudinaryImageTransforms({
      logoCloudinaryPath: projectMeta.logoCloudinaryPath,
    })

    return {
      props: {
        cloudinaryImageTransformation,
        locale,
        offers,
        projectMeta,
        ...(await loadTranslations(locale, ['common', 'pif', 'account'])),
      },
    }
  },
)

PayItForwardProjectCheckoutPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export default PayItForwardProjectCheckoutPage
